<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { faBars, faHome, faMagnifyingGlass, faStar } from '@fortawesome/duotone-regular-svg-icons'
import { useRouter } from '#imports'
import { useAuthStore } from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { useAppColor } from '~/composables/useAppColor'
import useAppBanner from '~/composables/banner'
import VinkeSterne from '~/assets/lottie/Vinke_Stjerne.json'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'
import AuDrawer from '~/components/ui/AuDrawer.vue'
import SettingsButton from '~/components/settings/SettingsButton.vue'
import AppLogo from '~/components/AppLogo.vue'

const { t } = useI18n()
const router = useRouter()
const { banner, headerColor } = useAppBanner()
const breakpoints = useBreakpoints(breakpointsTailwind)
const { isAuthenticated, hasOrganization, isTeacher } = storeToRefs(useAuthStore())
const { themeColor } = useAppColor()

const showMobileMenu = ref(false)
const smBreakpoint = breakpoints.greater('sm')

const textColor = computed(() => {
  const name = themeColor.value || ''
  const matches = /-(?:5|10|20)(?=\)?$)/.test(name)
  return matches ? 'light' : 'dark'
})

const currentRoute = computed(() => String(router.currentRoute.value.name ?? ''))

const isMainRoutes = computed(() => ['home', 'my_content', 'from_teacher', 'search']
  .some(p => currentRoute.value.includes(p)))

const isHomeRoute = computed(() => currentRoute.value === 'home')

const color = computed(() => isMainRoutes.value ? 'brand' : themeColor.value)

watch(smBreakpoint, () => showMobileMenu.value = !smBreakpoint.value && showMobileMenu.value)
</script>

<template>
  <header
    id="app-header"
    :class="banner ? 'absolute' : 'relative'"
    class="z-30 mx-auto w-full flex-col gap-6 bg-transparent py-6 sm:flex"
  >
    <nav
      class="max-w-screen-au m-auto flex w-full items-center gap-4 px-4 sm:px-8"
      :class="[
        headerColor ? 'text-white' : textColor,
        isAuthenticated ? 'justify-between' : 'justify-center',
      ]"
    >
      <div v-if="isAuthenticated" class="flex grow basis-0 items-center gap-4">
        <Tooltip :content="t('home.title')">
          <Button
            variant="solid"
            :shade="textColor"
            :color
            shape="square"
            size="xl"
            :data-pendo="PendoFeature.Navigation.Home"
            :to="{ name: 'home' }"
          >
            <font-awesome-icon
              :icon="faHome"
              size="lg"
            />
          </Button>
        </Tooltip>
        <div id="header-navigation" class="flex items-center gap-4" />
      </div>
      <AppLogo />

      <AuDrawer
        :title="t('presentation.menu.menu')"
        :open="showMobileMenu"
        @close="showMobileMenu = false"
      >
        <template #content>
          <div class="mt-8 flex flex-col gap-3">
            <Button
              size="xl"
              :to="{ name: 'search' }"
              :data-pendo="PendoFeature.Navigation.Search"
              @click="showMobileMenu = false"
            >
              <font-awesome-icon
                :icon="faMagnifyingGlass"
                size="lg"
              />
              {{ t('search.title') }}
            </Button>
            <Button
              size="xl"
              :to="{ name: isTeacher ? 'my_content' : 'from_teacher' }"
              :data-pendo="PendoFeature.Navigation.Shared"
              @click="showMobileMenu = false"
            >
              <font-awesome-icon
                :icon="faStar"
                size="lg"
              />
              {{ isTeacher ? t('myContent.title') : t('myContent.studentTitle') }}
            </Button>
            <SettingsButton @close="showMobileMenu = false" />
          </div>
        </template>
      </AuDrawer>
      <div class="flex grow basis-0 items-center justify-end gap-3 sm:hidden sm:px-0">
        <Button
          variant="solid"
          :shade="textColor"
          :color
          shape="square"
          size="xl"
          :data-pendo="PendoFeature.Navigation.Burger"
          @click="showMobileMenu = !showMobileMenu"
        >
          <font-awesome-icon :icon="faBars" />
        </Button>
      </div>
      <div
        v-if="isAuthenticated"
        class="hidden grow basis-0 items-center justify-end gap-3 pr-4 pl-2 sm:flex sm:px-0"
      >
        <Tooltip :content="t('search.title')">
          <Button
            variant="solid"
            :shade="textColor"
            :color
            shape="square"
            size="xl"
            :data-pendo="PendoFeature.Navigation.Search"
            :to="{ name: 'search' }"
          >
            <font-awesome-icon
              :icon="faMagnifyingGlass"
              size="lg"
            />
          </Button>
        </Tooltip>
        <Tooltip :content="isTeacher ? t('myContent.title') : t('myContent.studentTitle')">
          <Button
            v-if="hasOrganization"
            variant="solid"
            shape="square"
            :shade="textColor"
            :color
            size="xl"
            :data-pendo="PendoFeature.Navigation.Shared"
            :to="{ name: isTeacher ? 'my_content' : 'from_teacher' }"
          >
            <LottieAnimation
              v-if="isHomeRoute"
              :animation-data="VinkeSterne"
              :loop="false"
              class="size-5"
            />
            <font-awesome-icon
              v-else
              :icon="faStar"
              size="lg"
            />
          </Button>
        </Tooltip>
        <SettingsButton
          :shade="textColor"
          :color
        />
      </div>
    </nav>
  </header>
</template>
